import {
  Box,
  Button,
  Checkbox,
  Modal,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import {
  scheduledEmailsDetailedKeys,
  scheduledSlackMessagesKeys,
} from "../../../../api/queryKeys";
import TextButton from "../../../../components/buttons/TextButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { getPrettyDateTime } from "../../../../utils/timestamp";

import EmailEditor from "./notifications/EmailEditor";
import EmailListItem from "./notifications/EmailListItem";
import FilterBar from "./notifications/FilterBar";
import { generateBreezeEmailTemplate } from "./notifications/templateGenerator";
import { useFilteredData } from "./notifications/useFilteredData";

const EditScheduledNotificationsModal = ({ open, handleClose }) => {
  const colors = useColors();
  const queryClient = useQueryClient();
  const {
    getScheduledEmailsDetailed,
    getScheduledSlackMessages,
    updateScheduledEmailJob,
    deleteScheduledEmailJob,
    batchDeleteScheduledEmailJobs,
    createScheduledEmailJob,
    removeSlackScheduledMessages,
    refreshEmailScheduler,
    getBreezePlatformURL,
  } = useAPI();

  const [activeTab, setActiveTab] = useState(0);
  const [editingEmail, setEditingEmail] = useState(null);
  const [creatingEmail, setCreatingEmail] = useState(false);
  const [platformUrl, setPlatformUrl] = useState(null);
  const DEFAULT_SOURCE_EMAIL = "notifications@breezesec.com";
  const DEFAULT_SCHEDULE_DELAY_MS = 3600000; // 1 hour

  // Fetch platform URL when modal opens
  useEffect(() => {
    if (open && !platformUrl) {
      getBreezePlatformURL()
        .then((response) => {
          setPlatformUrl(response.data);
        })
        .catch((error) => {
          console.error("Failed to fetch platform URL:", error);
          // Fallback to default URL
          setPlatformUrl("https://app.breeze-security.com");
        });
    }
  }, [open, platformUrl, getBreezePlatformURL]);

  // Queries
  const scheduledEmailsQuery = useQuery({
    queryKey: scheduledEmailsDetailedKeys.all,
    queryFn: getScheduledEmailsDetailed,
    select: (response) => response.data,
    enabled: open,
  });

  const scheduledSlackMessagesQuery = useQuery({
    queryKey: scheduledSlackMessagesKeys.all,
    queryFn: getScheduledSlackMessages,
    select: (response) => response.data,
    enabled: open,
  });

  // Filter predicates
  const emailFilterPredicate = (email, filterValue) =>
    email.customer_id.toLowerCase().includes(filterValue.toLowerCase());

  const slackFilterPredicate = (message, filterValue) =>
    message.customer_id.toLowerCase().includes(filterValue.toLowerCase());

  // Key functions for unique identification
  const getEmailKey = (email) => `${email.customer_id}-${email.job_id}`;

  const getSlackKey = (message) =>
    `${message.customer_id}-${message.channel_id}-${message.id}`;

  // Use filtered data hooks
  const emailFilter = useFilteredData(
    scheduledEmailsQuery.data,
    emailFilterPredicate,
    getEmailKey
  );

  const slackFilter = useFilteredData(
    scheduledSlackMessagesQuery.data,
    slackFilterPredicate,
    getSlackKey
  );

  // Mutations
  const updateEmailMutation = useMutation({
    mutationFn: ({ customerId, jobId, updateData }) =>
      updateScheduledEmailJob(customerId, jobId, updateData),
    onSuccess: () => {
      Toast.success("Email notification updated successfully");
      setEditingEmail(null);
      queryClient.invalidateQueries({
        queryKey: scheduledEmailsDetailedKeys.all,
      });
      refreshEmailScheduler();
    },
    onError: (error) => {
      Toast.error(
        `Failed to update email: ${error.message || "Unknown error"}`
      );
    },
  });

  const deleteEmailMutation = useMutation({
    mutationFn: ({ customerId, jobId }) =>
      deleteScheduledEmailJob(customerId, jobId),
    onSuccess: () => {
      Toast.success("Email notification deleted successfully");
      queryClient.invalidateQueries({
        queryKey: scheduledEmailsDetailedKeys.all,
      });
      refreshEmailScheduler();
    },
    onError: (error) => {
      Toast.error(
        `Failed to delete email: ${error.message || "Unknown error"}`
      );
    },
  });

  const batchDeleteEmailMutation = useMutation({
    mutationFn: batchDeleteScheduledEmailJobs,
    onSuccess: () => {
      Toast.success("Selected email notifications deleted successfully");
      emailFilter.clearSelection();
      queryClient.invalidateQueries({
        queryKey: scheduledEmailsDetailedKeys.all,
      });
      refreshEmailScheduler();
    },
    onError: (error) => {
      Toast.error(
        `Failed to delete emails: ${error.message || "Unknown error"}`
      );
    },
  });

  const createEmailMutation = useMutation({
    mutationFn: createScheduledEmailJob,
    onSuccess: () => {
      Toast.success("Email notification created successfully");
      setEditingEmail(null);
      setCreatingEmail(false);
      queryClient.invalidateQueries({
        queryKey: scheduledEmailsDetailedKeys.all,
      });
      refreshEmailScheduler();
    },
    onError: (error) => {
      Toast.error(
        `Failed to create email: ${error.message || "Unknown error"}`
      );
    },
  });

  const batchDeleteSlackMutation = useMutation({
    mutationFn: removeSlackScheduledMessages,
    onSuccess: () => {
      Toast.success("Selected Slack messages deleted successfully");
      slackFilter.clearSelection();
      queryClient.invalidateQueries({
        queryKey: scheduledSlackMessagesKeys.all,
      });
    },
    onError: (error) => {
      Toast.error(
        `Failed to delete Slack messages: ${error.message || "Unknown error"}`
      );
    },
  });

  const handleEmailEdit = (email) => {
    setEditingEmail({
      ...email,
      scheduled_time: new Date(email.scheduled_time * 1000),
    });
  };

  const handleCreateNewEmail = () => {
    setCreatingEmail(true);
    setEditingEmail({
      customer_id: "",
      subject: "New Breeze Notification",
      to_addresses: [],
      bcc_addresses: [],
      html: generateBreezeEmailTemplate(platformUrl),
      scheduled_time: new Date(Date.now() + DEFAULT_SCHEDULE_DELAY_MS),
      source: DEFAULT_SOURCE_EMAIL,
    });
  };

  const handleDuplicateEmail = (email) => {
    setCreatingEmail(true);
    setEditingEmail({
      ...email,
      customer_id: email.customer_id,
      subject: `[COPY] ${email.subject}`,
      scheduled_time: new Date(Date.now() + DEFAULT_SCHEDULE_DELAY_MS),
      source: DEFAULT_SOURCE_EMAIL,
      job_id: undefined,
    });
  };

  const handleEmailSave = (editedEmail) => {
    if (!editedEmail) return;

    const emailData = {
      customer_id: editedEmail.customer_id,
      subject: editedEmail.subject,
      html: editedEmail.html,
      to_addresses: editedEmail.to_addresses,
      bcc_addresses: editedEmail.bcc_addresses,
      scheduled_time: Math.floor(editedEmail.scheduled_time.getTime() / 1000),
      source: editedEmail.source || DEFAULT_SOURCE_EMAIL,
    };

    if (creatingEmail) {
      createEmailMutation.mutate(emailData);
    } else {
      updateEmailMutation.mutate({
        customerId: editedEmail.customer_id,
        jobId: editedEmail.job_id,
        updateData: emailData,
      });
    }
  };

  const handleEmailDelete = (email) => {
    if (
      window.confirm(
        `Are you sure you want to delete this email notification?\n\nSubject: ${
          email.subject
        }\nTo: ${email.to_addresses?.join(", ")}`
      )
    ) {
      deleteEmailMutation.mutate({
        customerId: email.customer_id,
        jobId: email.job_id,
      });
    }
  };

  const handleBatchDelete = () => {
    if (emailFilter.selectedItems.length === 0) {
      Toast.error("Please select emails to delete");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${emailFilter.selectedItems.length} selected email notifications?`
      )
    ) {
      batchDeleteEmailMutation.mutate(emailFilter.selectedItems);
    }
  };

  const handleBatchDeleteSlack = () => {
    if (slackFilter.selectedItems.length === 0) {
      Toast.error("Please select Slack messages to delete");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${slackFilter.selectedItems.length} selected Slack messages?`
      )
    ) {
      batchDeleteSlackMutation.mutate(slackFilter.selectedItems);
    }
  };

  // Get unique customer IDs for filter dropdowns
  const getUniqueEmailCustomerIds = () => {
    if (!scheduledEmailsQuery.data) return [];
    const customerIds = scheduledEmailsQuery.data.map(
      (email) => email.customer_id
    );
    return [...new Set(customerIds)].sort();
  };

  const getUniqueSlackCustomerIds = () => {
    if (!scheduledSlackMessagesQuery.data) return [];
    const customerIds = scheduledSlackMessagesQuery.data.map(
      (message) => message.customer_id
    );
    return [...new Set(customerIds)].sort();
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 1000,
    maxHeight: "90vh",
    bgcolor: colors.common_white,
    borderRadius: 2,
    p: 3,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    overflow: "auto",
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6">Edit Email Notifications</Typography>
        <Typography variant="body2" color="textSecondary">
          View and edit pending email and Slack notifications before they are
          sent.
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
          >
            <Tab label="Email Notifications" />
            <Tab label="Slack Messages" />
          </Tabs>
        </Box>

        {/* Email Notifications Tab */}
        {activeTab === 0 && (
          <Box>
            {/* Filter and Batch Actions */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <FilterBar
                filterValue={emailFilter.filterValue}
                onFilterChange={emailFilter.setFilterValue}
                uniqueValues={getUniqueEmailCustomerIds()}
                filterLabel="Filter by Customer ID"
                allLabel="All Customers"
                showSelectAll={true}
                isAllSelected={emailFilter.isAllSelected}
                hasSelection={emailFilter.hasSelection}
                onSelectAll={emailFilter.handleSelectAll}
                selectedCount={emailFilter.selectedItems.length}
                totalCount={emailFilter.filteredData.length}
              />

              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleCreateNewEmail}
                >
                  Create New Email
                </Button>
                {emailFilter.selectedItems.length > 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={handleBatchDelete}
                    disabled={batchDeleteEmailMutation.isPending}
                  >
                    Delete Selected ({emailFilter.selectedItems.length})
                  </Button>
                )}
              </Box>
            </Box>

            {scheduledEmailsQuery.isLoading ? (
              <LoadingIcon />
            ) : scheduledEmailsQuery.isSuccess ? (
              emailFilter.filteredData.length === 0 ? (
                <Typography>
                  {emailFilter.filterValue
                    ? `No scheduled email notifications found for customer: ${emailFilter.filterValue}`
                    : "No scheduled email notifications found"}
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  {emailFilter.filteredData.map((email) => (
                    <EmailListItem
                      key={getEmailKey(email)}
                      email={email}
                      isSelected={emailFilter.isItemSelected(email)}
                      onSelect={emailFilter.handleItemSelection}
                      onEdit={handleEmailEdit}
                      onDuplicate={handleDuplicateEmail}
                      onDelete={handleEmailDelete}
                      isDisabled={
                        updateEmailMutation.isPending ||
                        deleteEmailMutation.isPending ||
                        batchDeleteEmailMutation.isPending
                      }
                    />
                  ))}
                </Box>
              )
            ) : (
              <Typography color="error">
                Error loading scheduled emails
              </Typography>
            )}
          </Box>
        )}

        {/* Slack Messages Tab */}
        {activeTab === 1 && (
          <Box>
            {/* Filter and Batch Actions */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <FilterBar
                filterValue={slackFilter.filterValue}
                onFilterChange={slackFilter.setFilterValue}
                uniqueValues={getUniqueSlackCustomerIds()}
                filterLabel="Filter by Customer ID"
                allLabel="All Customers"
                showSelectAll={true}
                isAllSelected={slackFilter.isAllSelected}
                hasSelection={slackFilter.hasSelection}
                onSelectAll={slackFilter.handleSelectAll}
                selectedCount={slackFilter.selectedItems.length}
                totalCount={slackFilter.filteredData.length}
              />

              <Box display="flex" gap={1}>
                {slackFilter.selectedItems.length > 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={handleBatchDeleteSlack}
                    disabled={batchDeleteSlackMutation.isPending}
                  >
                    Delete Selected ({slackFilter.selectedItems.length})
                  </Button>
                )}
              </Box>
            </Box>

            {scheduledSlackMessagesQuery.isLoading ? (
              <LoadingIcon />
            ) : scheduledSlackMessagesQuery.isSuccess ? (
              slackFilter.filteredData.length === 0 ? (
                <Typography>
                  {slackFilter.filterValue
                    ? `No scheduled Slack messages found for customer: ${slackFilter.filterValue}`
                    : "No scheduled Slack messages found"}
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  {slackFilter.filteredData.map((message) => (
                    <Box
                      key={getSlackKey(message)}
                      border={1}
                      borderColor="grey.300"
                      borderRadius={1}
                      p={2}
                      sx={{
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Checkbox
                            checked={slackFilter.isItemSelected(message)}
                            onChange={(e) =>
                              slackFilter.handleItemSelection(
                                message,
                                e.target.checked
                              )
                            }
                            size="small"
                          />
                          <Typography variant="subtitle2">
                            <strong>Customer:</strong> {message.customer_id}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2">
                        <strong>Channel:</strong> {message.channel_id}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Message:</strong> {message.text}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Scheduled:</strong>{" "}
                        {getPrettyDateTime(message.post_at, true)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )
            ) : (
              <Typography color="error">
                Error loading scheduled Slack messages
              </Typography>
            )}
          </Box>
        )}

        {/* Email Edit Modal */}
        {editingEmail && (
          <EmailEditor
            email={editingEmail}
            isCreating={creatingEmail}
            onSave={handleEmailSave}
            onClose={() => {
              setEditingEmail(null);
              setCreatingEmail(false);
            }}
            isSaving={
              creatingEmail
                ? createEmailMutation.isPending
                : updateEmailMutation.isPending
            }
          />
        )}

        <Box display="flex" justifyContent="center" gap={2} mt={2}>
          <TextButton onClick={handleClose}>Close</TextButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default function EditScheduledNotifications() {
  const colors = useColors();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Button
        variant="outlined"
        sx={{
          backgroundColor: colors.secondary_main,
          "&:hover": {
            backgroundColor: colors.secondary_dark,
          },
        }}
        color="primary"
        onClick={() => setIsModalOpen(true)}
      >
        Edit Email Notifications
      </Button>
      {isModalOpen && (
        <EditScheduledNotificationsModal
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}
